import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Modal tạo dự án mới — thay thế window.prompt.
 *
 * Props:
 *   open      : boolean
 *   loading   : boolean (đang tạo)
 *   onCreate  : (name: string) => void | Promise<void>
 *   onClose   : () => void
 */
function CreateProjectModal({ open, loading = false, onCreate, onClose }) {
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setPrompt("");
      // Focus input sau khi modal mở
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed || loading) return;
    onCreate?.({ name: trimmed, prompt: prompt.trim() });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !loading && onClose?.()}
      style={{ animation: "fadeIn 0.12s ease-out" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "pimSlideUp 0.15s ease-out" }}
      >
        {/* Header */}
        <div className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M12 11v4m2-2h-4"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              Tạo dự án mới
            </h3>
            <p className="mt-1 text-sm text-gray-500 leading-relaxed">
              Nhóm các đoạn chat liên quan vào một dự án để dễ quản lý.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pb-1">
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Tên dự án
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            maxLength={100}
            placeholder="VD: Lớp 12, Ôn thi THPT QG…"
            disabled={loading}
            className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100 disabled:bg-gray-50"
          />
          <div className="mt-1.5 text-right text-[11px] text-gray-400">
            {trimmed.length}/100
          </div>

          <label className="block text-xs font-medium text-gray-600 mb-1.5 mt-2">
            Prompt dự án (tùy chọn)
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={10000}
            rows={5}
            placeholder="Hướng dẫn AI cách trả lời trong dự án này. VD: Bạn là gia sư Toán lớp 12, luôn giải chi tiết từng bước…"
            disabled={loading}
            className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100 disabled:bg-gray-50 resize-y"
          />
          <div className="mt-1 text-right text-[11px] text-gray-400">
            {prompt.length}/10000
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200/70 rounded-lg disabled:opacity-50 transition-colors"
          >
            Hủy
          </button>
          <button
            type="button"
            disabled={loading || !trimmed}
            onClick={submit}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            {loading && (
              <svg
                className="w-4 h-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            )}
            {loading ? "Đang tạo…" : "Tạo dự án"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default CreateProjectModal;
